import { RemitService } from '@app-core/services';
import { ContractType } from '@app-model/api';
import { ContractFilterGroupType, ContractFilterType } from '@app-model/remit-filter.model';

import * as RemitActions from './remit.actions';

export interface State {
  loadingPendingContracts: boolean;
  pendingContracts: ContractType[];
  pendingFilters: ContractFilterType[];
  pendingGridContracts: ContractType[];
  pendingToDeclineContracts: ContractType[];
  pendingToRemitContracts: ContractType[];
  submittedPendingContracts: ContractType[];

  remittedContracts: ContractType[];
  remittedFilters: ContractFilterGroupType[];
  remittedGridContracts: ContractType[];
}

const InitialState: State = {
  loadingPendingContracts: false,
  pendingContracts: [],
  pendingFilters: [],
  pendingGridContracts: [],
  pendingToDeclineContracts: [],
  pendingToRemitContracts: [],
  submittedPendingContracts: [],

  remittedContracts: [],
  remittedFilters: [],
  remittedGridContracts: [],
};

export function reducer(state = InitialState, action: RemitActions.All): State {
  switch (action.type) {
    case RemitActions.GET_PENDING_CONTRACTS: {
      return { ...state, loadingPendingContracts: true };
    }

    case RemitActions.GET_PENDING_CONTRACTS_SUCCESS: {
      return {
        ...state,
        loadingPendingContracts: false,
        pendingContracts: action.payload.contracts.pendingContracts,
        pendingFilters: action.payload.filters,
        pendingGridContracts: action.payload.contracts.pendingContracts,
        pendingToDeclineContracts: action.payload.contracts.pendingToDeclineContracts,
        pendingToRemitContracts: action.payload.contracts.pendingToRemitContracts,
      };
    }

    case RemitActions.FILTER_PENDING_GRID: {
      const pendingGridContracts = RemitService.filterPendingGrid(action.payload, state.pendingContracts);

      return { ...state, pendingGridContracts };
    }

    case RemitActions.ADD_TO_PENDING_REMIT: {
      // to mutate state without updating filters
      state.pendingGridContracts.forEach((item) => {
        if (item.econContractId === action.payload.econContractId) {
          item.addToRemit = true;
        }
      });

      return {
        ...state,
        pendingToRemitContracts: [...state.pendingToRemitContracts, action.payload],
      };
    }

    case RemitActions.DELETE_FROM_PENDING_REMIT: {
      // to mutate state without updating filters
      state.pendingGridContracts.forEach((item) => {
        if (item.econContractId === action.payload) {
          item.addToRemit = false;
        }
      });

      return {
        ...state,
        pendingToRemitContracts: state.pendingToRemitContracts.filter((item) => item.econContractId !== action.payload),
      };
    }

    case RemitActions.DECLINE_PENDING_CONTRACT: {
      const contracts = RemitService.declineAndUpdateContracts(state.pendingGridContracts, action.payload);

      return {
        ...state,
        pendingGridContracts: contracts,
        pendingToDeclineContracts: [...state.pendingToDeclineContracts, action.payload],
      };
    }

    case RemitActions.REINSTATE_PENDING_CONTRACT: {
      const contracts = RemitService.reinstateAndUpdateContracts(state.pendingGridContracts, action.payload);

      return {
        ...state,
        pendingGridContracts: contracts,
        pendingToDeclineContracts: state.pendingToDeclineContracts.filter(
          (item) => item.econContractId !== action.payload.econContractId
        ),
        pendingToRemitContracts: contracts.filter((item) => item.addToRemit === true),
      };
    }

    case RemitActions.ADD_ALL_TO_PENDING_REMIT: {
      const addedContracts = state.pendingGridContracts.map((item) => {
        return !item.dupError && !item.decline && !item.addToRemit && !item.isReadOnly
          ? { ...item, addToRemit: true, decline: false }
          : { ...item };
      });

      return {
        ...state,
        pendingGridContracts: addedContracts,
        pendingToRemitContracts: addedContracts.filter((item) => item.addToRemit),
      };
    }

    case RemitActions.REMOVE_ALL_FROM_PENDING_REMIT_ONLY: {
      return {
        ...state,
        pendingGridContracts: state.pendingGridContracts.map((item) =>
          item.addToRemit ? { ...item, addToRemit: false } : { ...item }
        ),
        pendingToRemitContracts: [],
      };
    }

    case RemitActions.REMOVE_ALL_FROM_DECLINED_REMIT_ONLY: {
      const contracts = state.pendingGridContracts.reduce((prevValue, elem) => {
        return RemitService.reinstateAndUpdateContracts(prevValue, elem);
      }, state.pendingGridContracts);

      return {
        ...state,
        pendingGridContracts: contracts,
        pendingToDeclineContracts: [],
        pendingToRemitContracts: contracts.filter((elem) => elem.addToRemit),
      };
    }

    case RemitActions.SUBMIT_ALL_PENDING_REMIT_SUCCESS: {
      return {
        ...state,
        pendingContracts: action.payload.contracts.pendingContracts,
        pendingGridContracts: action.payload.contracts.pendingContracts,
        pendingToRemitContracts: action.payload.contracts.pendingToRemitContracts,
        pendingToDeclineContracts: action.payload.contracts.pendingToDeclineContracts,
        pendingFilters: action.payload.filters,
        // copies over submitted request for printing
        submittedPendingContracts: action.payload.submittedRemittedRequest,
      };
    }

    case RemitActions.GET_REMITTED_CONTRACTS_SUCCESS: {
      return {
        ...state,
        remittedContracts: action.payload.contracts.remittedContracts,
        remittedGridContracts: [],
        remittedFilters: action.payload.filters,
      };
    }

    case RemitActions.FILTER_REMITTED_GRID: {
      const contracts = RemitService.filterRemittedGrid(action.payload, state.remittedContracts);

      return { ...state, remittedGridContracts: contracts };
    }

    default: {
      return state;
    }
  }
}
